import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { QuestionnaireSelectForm } from 'spa/member/components/WorkPreference/QuestionnaireSelectForm'
import { KarteInputForm } from 'spa/member/components/KarteInputForm'
import { MultiCheckBoxForm } from 'spa/member/components/MultiCheckBoxForm'
import { RadioForm } from 'spa/member/components/RadioForm'
import { DateForm } from 'spa/member/components/DateForm'
import { NumberSelectForm } from 'spa/member/components/NumberSelectForm'
import { TextAreaForm } from 'spa/member/components/TextAreaForm'
import { generateHoursPerWeekOptions } from 'spa/member/components/WorkPreference/functions'
import {
  workDaysOfWeekOptions,
  officeDaysOfWeekOptions,
  remoteWorkPreferenceOptions,
  availableTimeOptions,
  wishBooleanOptions,
  workFromAbroadOptions
} from 'spa/member/components/WorkPreference/constants'
import styles from './WorkPreferenceForm.module.scss'

const WorkDaysRecommendationMessage: React.FC = () => (
  <div id="work-days-recommendation-message" className="bg-tech-silver-10 py-1 mb-XS text-gray-800 text-sm text-left leading-6" style={{ paddingLeft: '5.5px', paddingRight: '5.5px' }}>
    <strong className="text-tech-red-100 font-bold">週4日以上</strong>の稼働をご検討いただけると、紹介できる案件が<strong className="text-tech-red-100 font-bold">最大約1.5倍</strong>になります！
  </div>
)

const minWorkHoursPerWeekOptions = generateHoursPerWeekOptions(8, 40, 1)
const maxWorkHoursPerWeekOptions = generateHoursPerWeekOptions(8, 40, 1)

export const StyledWorkPreferenceForm: React.FC = () => {
  const { setValue, getValues, trigger, formState: { dirtyFields, isLoading } } = useFormContext()
  const [lessWorkDays, setLessWorkDays] = useState(false)

  useEffect(() => {
    if (!isLoading) {
      setLessWorkDays((getValues('work_days_of_week') || []).length < 4)
    }
  }, [isLoading])

  return (
    <div className={styles.formOuter}>
      <DateForm
        name='start_date'
        label={
          <>
            <span className="tablet:hidden">稼働開始の希望時期</span>
            <span className="hidden tablet:inline">稼働開始の希望時期を教えてください</span>
          </>
        }
        required
        inputProps={{
          min: new Date().toISOString().split('T')[0],
          max: '9999-12-31',
        }}
        rules={{
          required: 'この質問は必須です',
          validate: (value) => {
            const year = parseInt(value.split('-')[0], 10)
            if (isNaN(year) || year > 9999) {
              return '年は4桁以内で入力してください'
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const selectedDate = new Date(value);
            return selectedDate >= today || '過去日付です。本日以降の日付を入力ください。';
          }
        }}
      />
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3XL">
        <QuestionnaireSelectForm
          name='min_work_hours_per_week'
          label='週の最小稼働時間'
          options={minWorkHoursPerWeekOptions}
          required
          rules={{
            required: 'この質問は必須です',
          }}
          onChange={(e) => {
            const hoursPerMonth = Number(e.target.value) * 4
            setValue('_min_work_hours_per_month', hoursPerMonth)
            getValues('max_work_hours_per_week') && trigger('max_work_hours_per_week')

            // 希望報酬額幅を再計算
            const desiredMonthlySalary = getValues('desired_monthly_salary')
            if (desiredMonthlySalary) {
              const desiredHourlySalary = Math.ceil(Number(desiredMonthlySalary) / 160)
              setValue('_min_desired_salary_range', (hoursPerMonth * desiredHourlySalary).toLocaleString())
            }
          }}
        />
        <QuestionnaireSelectForm
          name='max_work_hours_per_week'
          label='週の最大稼働時間'
          options={maxWorkHoursPerWeekOptions}
          required
          rules={{
            required: 'この質問は必須です',
            validate: (value: string) => {
              const minHours = getValues('min_work_hours_per_week')
              return !minHours || Number(value) >= Number(minHours) ||
                '週の最大稼働時間が週の最小稼働時間を下回っています'
            }
          }}
          onChange={(e) => {
            const hoursPerMonth = Number(e.target.value) * 4
            setValue('_max_work_hours_per_month', hoursPerMonth)

            // 希望報酬額幅を再計算
            const desiredMonthlySalary = getValues('desired_monthly_salary')
            if (desiredMonthlySalary) {
              const desiredHourlySalary = Math.ceil(Number(desiredMonthlySalary) / 160)
              setValue('_max_desired_salary_range', (hoursPerMonth * desiredHourlySalary).toLocaleString())
            }
          }}
        />
      </div>
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3XL">
        <KarteInputForm
          name='_min_work_hours_per_month'
          label='1か月で稼働可能な累計時間（最小）'
          noBadge={true}
          annotationNote="※週の最小稼働時間× 4週間で計算"
          inputProps={{
            type: 'number',
            disabled: true,
          }}
        />
        <KarteInputForm
          name='_max_work_hours_per_month'
          label='1か月で稼働可能な累計時間（最大）'
          noBadge={true}
          annotationNote="※週の最大稼働時間× 4週間で計算"
          inputProps={{
            type: 'number',
            disabled: true,
          }}
        />
      </div>
      <NumberSelectForm
        name='desired_monthly_salary'
        label={
          <>
            <span className="tablet:hidden">希望報酬額 （1.0人月換算/税別）</span>
            <span className="hidden tablet:inline">希望報酬額 （1.0人月換算/税別）を教えてください</span>
          </>
        }
        annotationNote='（1.0人月=月160時間に換算した金額）'
        start={200000}
        step={10000}
        end={2000000}
        placeholder='選択してください'
        required
        rules={{
          required: 'この質問は必須です'
        }}
        onChange={(e) => {
          const desiredMonthlySalary = Number(e.target.value)
          const desiredHourlySalary = Math.ceil(desiredMonthlySalary / 160)
          setValue('_desired_hourly_salary', desiredHourlySalary.toLocaleString())

          // 希望報酬額幅を再計算
          const minWorkHoursPerMonth = getValues('_min_work_hours_per_month')
          const maxWorkHoursPerMonth = getValues('_max_work_hours_per_month')
          if (minWorkHoursPerMonth) {
            setValue('_min_desired_salary_range', (minWorkHoursPerMonth * desiredHourlySalary).toLocaleString())
          }
          if (maxWorkHoursPerMonth) {
            setValue('_max_desired_salary_range', (maxWorkHoursPerMonth * desiredHourlySalary).toLocaleString())
          }
        }}
      />
      <KarteInputForm
        name='_desired_hourly_salary'
        label='希望報酬額（時給換算/税別）を教えてください'
        noBadge={true}
        required
        inputProps={{
          disabled: true,
        }}
      />
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3XL">
        <KarteInputForm
          name='_min_desired_salary_range'
          label='希望報酬額幅（最小）'
          noBadge={true}
          annotationNote="※1か月で稼働可能な累計時間（最小） × 時給換算額"
          inputProps={{
            disabled: true,
          }}
        />
        <KarteInputForm
          name='_max_desired_salary_range'
          label='希望報酬額幅（最大）'
          noBadge={true}
          annotationNote="※1か月で稼働可能な累計時間（最大） × 時給換算額"
          inputProps={{

            disabled: true,
          }}
        />
      </div>
      <MultiCheckBoxForm
        name='work_days_of_week'
        label='稼働可能な曜日'
        annotationNote='※複数選択可'
        options={workDaysOfWeekOptions}
        rules={{
          required: 'この質問は必須です',
          validate: (value: number[]) => value.length > 0 || 'この質問は必須です'
        }}
        onChange={() => {
          const workDays = getValues('work_days_of_week')
          const allSelected = workDays.includes('all')
          setLessWorkDays(workDays.length < 4 && !allSelected)
        }}
        messageInMiddle={lessWorkDays && <WorkDaysRecommendationMessage />}
        required
      />
      <MultiCheckBoxForm
        name='office_days_of_week'
        label='稼働先オフィスへの出社が可能な曜日'
        annotationNote='※複数選択可'
        options={officeDaysOfWeekOptions}
        rules={{
          required: 'この質問は必須です',
          validate: (value: number[]) => value.length > 0 || 'この質問は必須です'
        }}
        required
      />
      <RadioForm
        name='remote_work_preference'
        label={(<>フルリモートを希望の方へ<br />下記3つから一番近いものをお答えください</>)}
        options={remoteWorkPreferenceOptions}
      />
      <RadioForm
        name='available_time'
        label={
          <>
            <span className="tablet:hidden">稼働可能な時間帯</span>
            <span className="hidden tablet:inline">稼働可能な時間帯を教えてください</span>
          </>
        }
        options={availableTimeOptions}
        required
        rules={{
          required: 'この質問は必須です'
        }}
      />
      <TextAreaForm
        name='additional_conditions'
        label='希望条件への補足があれば教えてください'
        annotationNote='（金額、業務内容、稼働時間帯、など）'
        rules={{
          maxLength: {
            value: 1000,
            message: '1000文字以内で入力してください'
          }
        }}
      />
      <RadioForm
        name='full_time_job_introduction'
        label='正社員案件のご紹介についてのご希望を教えてください'
        options={wishBooleanOptions}
        required
        rules={{
          required: 'この質問は必須です'
        }}
      />
      <RadioForm
        name='temp_job_introduction'
        label='派遣案件のご紹介についてのご希望を教えてください'
        options={wishBooleanOptions}
        required
        rules={{
          required: 'この質問は必須です'
        }}
      />
      <TextAreaForm
        name='pre_communication_notes'
        label='ご参画前に稼働先へお伝えしておいた方がよいことがございましたら教えてください'
        rules={{
          maxLength: {
            value: 1000,
            message: '1000文字以内で入力してください'
          }
        }}
      />

      {/* FourthForm - その他 */}
      <TextAreaForm
        name='excluded_companies'
        label='ご推薦を控えてほしい企業名があれば教えてください'
        annotationNote='※企業名が複数ある場合はカンマ区切りで入力してください'
        textAreaProps={{ placeholder: '株式会社○○○○,株式会社××' }}
        rules={{
          maxLength: {
            value: 1000,
            message: '1000文字以内で入力してください'
          }
        }}
      />
      <RadioForm
        name='work_from_abroad'
        label='稼働場所について'
        annotationNote={
          <>
            ※海外からご稼働いただく場合、クラウドテックからお支払いする報酬は不課税になります
            <br />
            ※お振込口座は日本国内の銀行口座のみとなります（※Paypalは使用不可）
          </>
        }
        options={workFromAbroadOptions}
        required
        rules={{
          required: 'この質問は必須です'
        }}
      />
    </div>
  )
}
